/**
 * ProUpgradeNotice
 *
 * Centered explanation modal shown when a user crosses from a free feature
 * into a Pro-only one (e.g., "Planning around logged meals"). The goal is to
 * REPLACE the previous "failed/glitchy" toast experience with a clear, calm
 * message that tells the user exactly which boundary they crossed, then hands
 * them off to the paywall on confirm.
 *
 * Usage: rendered globally by SubscriptionContext when a 402 with code
 * `planning_around_logged_meals_requires_pro` (or similar future codes) is
 * received. The provider supplies open/title/message and the upgrade CTA
 * handler.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { Lock } from 'lucide-react';
import { Button } from './ui/button';

export function ProUpgradeNotice({ isOpen, title, message, onUpgrade, onClose }) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        // z-[60] sits above PlanFoodLauncher (z-50) so it always wins when
        // a 402 arrives mid-generation. Paywall also uses z-50 but renders
        // separately and AFTER this notice closes.
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="pro-upgrade-notice-overlay"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 16 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 22, stiffness: 320 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-sm bg-background rounded-2xl shadow-xl p-6 text-center"
          data-testid="pro-upgrade-notice-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pro-upgrade-notice-title"
        >
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-accent to-cyan-400 flex items-center justify-center">
            <Lock size={26} className="text-white" />
          </div>

          <h2
            id="pro-upgrade-notice-title"
            className="font-heading font-bold text-xl mb-2"
            data-testid="pro-upgrade-notice-title"
          >
            {title}
          </h2>

          <p
            className="text-sm text-muted-foreground mb-6 leading-relaxed"
            data-testid="pro-upgrade-notice-message"
          >
            {message}
          </p>

          <Button
            onClick={onUpgrade}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-accent to-cyan-500 text-white font-semibold hover:opacity-90"
            data-testid="pro-upgrade-notice-cta"
          >
            See Fitxess Pro
          </Button>

          <button
            onClick={onClose}
            className="w-full mt-3 text-sm text-muted-foreground hover:text-foreground py-2"
            data-testid="pro-upgrade-notice-dismiss"
          >
            Not now
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default ProUpgradeNotice;
